"use client";

import { fetchBanner, getBannerLink } from "@/utils/topsortUtil";

import { useAppSelector } from "@/store/hooks";
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Carousel } from "react-bootstrap";

import CaretLeftIcon from "public/next-public/common/icons/caret-solid-left.svg";
import CaretRightIcon from "public/next-public/common/icons/caret-solid-right.svg";
import PauseIcon from "public/next-public/common/icons/pause-icon.svg";
import PlayIcon from "public/next-public/common/icons/play-icon.svg";
import style from "./topsortBanner.module.scss";

enum SlideStatus {
  NotLoaded = 0,
  Loading = 1,
  Loaded = 2,
  NotAvailable = 3,
}

// ---- Config ----
const TOTAL_SLOTS = 5; // slots 1..5
const INTERVAL_MS = 7000; // autoplay timing

const HOME_PAGE_BANNERS_PREFIX = "hp-slot-";

// Bootstrap `md`. Below this the 1:1 creative is served, at or above it the 16:4 one.
// Matched with matchMedia rather than a measured width so the creative we request always
// agrees with the aspect ratio the stylesheet applies — a measured width excludes the
// scrollbar and would disagree with the media query for ~15px either side of the boundary.
// Keep in sync with the media-breakpoint-down(md) block in topsortBanner.module.scss.
const WIDE_BANNER_MEDIA_QUERY = "(min-width: 768px)";

const TopsortBanner: FunctionComponent<TopSortBannerProps> = ({}) => {
  const topsortJsApiKey = useAppSelector((state) => state.config.topsortJsApiKey);
  const [isWideViewport, setIsWideViewport] = useState<boolean>(() =>
    typeof window === "undefined" ? true : window.matchMedia(WIDE_BANNER_MEDIA_QUERY).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(WIDE_BANNER_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsWideViewport(event.matches);

    setIsWideViewport(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const topsortBannerType = useMemo(() => {
    return isWideViewport ? "desktop" : "mobile";
  }, [isWideViewport]);

  const [statuses, setStatuses] = useState<SlideStatus[]>(() =>
    Array.from({ length: TOTAL_SLOTS }, () => SlideStatus.NotLoaded),
  );

  const [banners, setBanners] = useState<(Winner | null)[]>(() => Array.from({ length: TOTAL_SLOTS }, () => null));

  const [requestedSlots, setRequestedSlots] = useState<Set<number>>(new Set());

  const [lastLoadedIndex, setLastLoadedIndex] = useState<number>(-1);

  const [bannersLoaded, setBannersLoaded] = useState<number>(0);

  const [nextBannerToLoad, setNextBannerToLoad] = useState<number | null>(null);

  const [previousBannerType, setPreviousBannerType] = useState<string>(topsortBannerType);

  const shouldRestartBootstrap = useRef(false);

  const wrap = useCallback((n: number) => ((n % TOTAL_SLOTS) + TOTAL_SLOTS) % TOTAL_SLOTS, []);
  const nextSlot = useCallback((i: number) => wrap(i + 1), [wrap]);

  const [activeVisibleIndex, setActiveVisibleIndex] = useState(0);

  const findNextSelectable = useCallback(
    (start: number, direction: 1 | -1 = 1): number | null => {
      for (let step = 0; step < TOTAL_SLOTS; step++) {
        const idx = wrap(start + step * direction);
        const status = statuses[idx];

        if (status !== SlideStatus.NotAvailable) {
          return idx;
        }
      }
      return null;
    },
    [statuses, wrap],
  );

  const loadSlide = useCallback(
    async (idx: number, isFallback: boolean = false) => {
      if (idx < 0 || idx >= TOTAL_SLOTS) return;
      const st = statuses[idx];
      if (st === SlideStatus.Loaded || st === SlideStatus.NotAvailable || st === SlideStatus.Loading) return;

      setStatuses((s) => {
        const copy = [...s];
        copy[idx] = SlideStatus.Loading;
        return copy;
      });

      try {
        const auctionType = "landing";
        const slodName = `${HOME_PAGE_BANNERS_PREFIX}${idx + 1}`;

        const banner = await fetchBanner(auctionType, slodName, topsortJsApiKey, topsortBannerType);

        if (!banner || !banner.found) {
          setStatuses((s) => {
            const copy = [...s];
            copy[idx] = SlideStatus.NotAvailable;
            return copy;
          });
          setBanners((b) => {
            const copy = [...b];
            copy[idx] = null;
            return copy;
          });

          setRequestedSlots((prev) => new Set(prev).add(idx));

          if (nextBannerToLoad === idx) {
            let nextSlotToTry = nextSlot(idx);
            let attempts = 0;

            while (attempts < TOTAL_SLOTS && nextSlotToTry < TOTAL_SLOTS) {
              if (!requestedSlots.has(nextSlotToTry)) {
                setNextBannerToLoad(nextSlotToTry);
                break;
              }
              nextSlotToTry = nextSlot(nextSlotToTry);
              attempts++;
            }

            if (attempts >= TOTAL_SLOTS || nextSlotToTry >= TOTAL_SLOTS) {
              setNextBannerToLoad(null);
            }
          }

          return;
        }
        setBanners((b) => {
          const copy = [...b];
          copy[idx] = banner.winner!;
          return copy;
        });
        setStatuses((s) => {
          const copy = [...s];
          copy[idx] = SlideStatus.Loaded;
          return copy;
        });

        setLastLoadedIndex(Math.max(lastLoadedIndex, idx));

        setBannersLoaded((prev) => prev + 1);

        const nextSlotIdx = nextSlot(idx);
        if (nextSlotIdx < TOTAL_SLOTS && !requestedSlots.has(nextSlotIdx)) {
          setNextBannerToLoad(nextSlotIdx);
        }
      } catch {
        setStatuses((s) => {
          const copy = [...s];
          copy[idx] = SlideStatus.NotAvailable;
          return copy;
        });
        setBanners((b) => {
          const copy = [...b];
          copy[idx] = null;
          return copy;
        });
      }
    },
    [statuses, lastLoadedIndex],
  );

  useEffect(() => {
    const first = findNextSelectable(0, 1);
    if (first != null) {
      loadSlide(first, false);
      setLastLoadedIndex(first);
      setBannersLoaded(1);

      const second = nextSlot(first);
      if (second < TOTAL_SLOTS) {
        setNextBannerToLoad(second);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (topsortBannerType !== previousBannerType) {
      // Reset all banner states
      setStatuses(Array.from({ length: TOTAL_SLOTS }, () => SlideStatus.NotLoaded));
      setBanners(Array.from({ length: TOTAL_SLOTS }, () => null));
      setRequestedSlots(new Set());

      setLastLoadedIndex(-1);
      setBannersLoaded(0);
      setNextBannerToLoad(null);
      setActiveVisibleIndex(0);

      setPreviousBannerType(topsortBannerType);

      shouldRestartBootstrap.current = true;
    }
  }, [topsortBannerType, previousBannerType]);

  useEffect(() => {
    if (shouldRestartBootstrap.current && bannersLoaded === 0) {
      const first = findNextSelectable(0, 1);
      if (first != null) {
        loadSlide(first, false);
        setLastLoadedIndex(first);
        setBannersLoaded(1);

        const second = nextSlot(first);
        if (second < TOTAL_SLOTS) {
          setNextBannerToLoad(second);
        }
      }

      shouldRestartBootstrap.current = false;
    }
  }, [bannersLoaded, findNextSelectable, loadSlide, nextSlot]);

  const visibleSlots: number[] = useMemo(
    () =>
      banners
        .map((b, idx) => ({ b, idx }))
        .filter(({ b, idx }) => b && statuses[idx] === SlideStatus.Loaded)
        .map(({ idx }) => idx),
    [banners, statuses],
  );

  useEffect(() => {
    if (visibleSlots.length === 0) {
      setActiveVisibleIndex(0);
      return;
    }
    if (activeVisibleIndex >= visibleSlots.length) {
      setActiveVisibleIndex(visibleSlots.length - 1);
    }
  }, [visibleSlots, activeVisibleIndex, lastLoadedIndex]);

  const handleSelect = (selectedVisibleIdx: number) => {
    setActiveVisibleIndex(selectedVisibleIdx);

    if (selectedVisibleIdx === visibleSlots.length - 1 && nextBannerToLoad !== null) {
      setRequestedSlots((prev) => new Set(prev).add(nextBannerToLoad));
      loadSlide(nextBannerToLoad, false);

      setNextBannerToLoad(null);
    }
  };

  useEffect(() => {
    if (visibleSlots.length === 0) return;

    if (activeVisibleIndex === visibleSlots.length - 1 && nextBannerToLoad !== null) {
      setRequestedSlots((prev) => new Set(prev).add(nextBannerToLoad));
      loadSlide(nextBannerToLoad, false);

      setNextBannerToLoad(null);
    }
  }, [activeVisibleIndex, visibleSlots, bannersLoaded, nextBannerToLoad, requestedSlots, loadSlide]);

  const [isPaused, setIsPaused] = useState(false);

  const handlePrevNext = (direction: 1 | -1) => {
    const newIndex = activeVisibleIndex + direction;

    if (newIndex < 0) {
      setActiveVisibleIndex(visibleSlots.length - 1);
    } else if (newIndex >= visibleSlots.length) {
      setActiveVisibleIndex(0);
    } else {
      setActiveVisibleIndex(newIndex);
    }
  };

  const carouselRef = useRef<any>(null);

  const items = visibleSlots.map((slotIdx) => {
    const banner = banners[slotIdx]!;
    return (
      <Carousel.Item key={slotIdx}>
        <div className={style.bannerItem}>
          <a
            href={getBannerLink(banner)}
            data-ts-product={banner.type === "product" ? banner.id : ""}
            data-ts-resolved-bid={banner.resolvedBidId}
            data-ts-clickable={true}
          >
            <picture
              {...(banner.type === "product" ? { "data-ts-product": banner.id } : {})}
              {...(banner.resolvedBidId
                ? { "data-ts-resolved-bid": banner.resolvedBidId, "data-ts-clickable": true }
                : {})}
            >
              <source media="(max-width: 767.98px)" srcSet={banner.asset[1]?.url} width="100%" height={650} />
              <source media="(min-width: 768px)" srcSet={banner.asset[0]?.url} width="100%" height={400} />
              <img className={style.bannerImage} alt="" src={banner.asset[0]?.url} width="100%" height={400} />
            </picture>
          </a>
        </div>
      </Carousel.Item>
    );
  });

  if (visibleSlots.length === 0) {
    return <div className={style.noBanners} />;
  }

  return (
    <div id="topsort-banner" className={style.topSortBanner}>
      <Carousel
        ref={carouselRef}
        className={style.topSortCarousel}
        activeIndex={activeVisibleIndex}
        onSelect={handleSelect}
        controls={false}
        indicators={false}
        interval={isPaused ? null : INTERVAL_MS} // null = paused; number = auto-advance
        pause="hover"
        touch
        wrap
      >
        {items}
      </Carousel>

      <div className={style.navigator}>
        <button
          type="button"
          aria-label="Previous banner"
          className={style.navBtn + " " + style.prev}
          onClick={() => handlePrevNext(-1)}
        >
          <CaretLeftIcon className={style.navBtnIcon} height={24} width={24} />
        </button>
        <button
          type="button"
          aria-label={isPaused ? "Play banner rotation" : "Pause banner rotation"}
          className={style.navBtn + " " + (isPaused ? style.play : style.pause)}
          onClick={() => setIsPaused((p) => !p)}
        >
          {isPaused ? (
            <PlayIcon className={style.navBtnIcon} height={24} width={24} />
          ) : (
            <PauseIcon className={style.navBtnIcon + " " + style.pauseIcon} height={24} width={24} />
          )}
        </button>
        <button
          type="button"
          aria-label="Next banner"
          className={style.navBtn + " " + style.next}
          onClick={() => handlePrevNext(1)}
        >
          <CaretRightIcon className={style.navBtnIcon} height={24} width={24} />
        </button>
      </div>
    </div>
  );
};

export default TopsortBanner;

export type TopSortBannerProps = Record<string, never>;
